import React, { useState, useEffect } from 'react';
import { Download, Bell, Smartphone, CheckCircle, X, MapPin, Sparkles, ShieldCheck, Check, AlertCircle } from 'lucide-react';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { pushNotificationService } from '../../services/pushNotificationService';
import { useAreaMotoristaOptional } from '../../contexts/AreaMotoristaContext';

export const PWAInstallBanner: React.FC = () => {
  const { isInstallable, isInstalled, isIOS, install } = usePWAInstall();
  const areaMotorista = useAreaMotoristaOptional();

  // Estado do Pop-up
  const [isOpen, setIsOpen] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [notifPermission, setNotifPermission] = useState<NotificationPermission | 'unsupported'>('default');
  const [gpsStatus, setGpsStatus] = useState<'prompt' | 'granted' | 'denied' | 'unknown'>('unknown');
  const [gpsFeedbackMsg, setGpsFeedbackMsg] = useState<string | null>(null);
  const [isGettingGps, setIsGettingGps] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // Verifica se tudo já está 100% concedido
  const isAllGranted = notifPermission === 'granted' && (gpsStatus === 'granted') && isInstalled;

  useEffect(() => {
    // Checar Notificação
    const initialNotif = pushNotificationService.getPermissionStatus();
    setNotifPermission(initialNotif);

    // Checar GPS
    if (typeof navigator !== 'undefined' && 'permissions' in navigator && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' as PermissionName }).then((result) => {
        setGpsStatus(result.state as 'prompt' | 'granted' | 'denied');
        result.onchange = () => {
          setGpsStatus(result.state as 'prompt' | 'granted' | 'denied');
        };
      }).catch(() => {
        setGpsStatus('unknown');
      });
    }

    // Verificar se o usuário já dispensou permanentemente ou nesta sessão
    const dismissedPermanently = localStorage.getItem('voope_pwa_popup_dismissed_permanently') === 'true';
    const sessionDismissed = sessionStorage.getItem('voope_pwa_popup_session_dismissed') === 'true';

    // Abrir o pop-up automaticamente após 1.5s se houver algo pendente e não estiver dispensado
    const timer = setTimeout(() => {
      const needsAttention = initialNotif !== 'granted' || !isInstalled;
      if (needsAttention && !dismissedPermanently && !sessionDismissed) {
        setIsOpen(true);
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [isInstalled]);

  // Fechar nesta sessão
  const handleCloseSession = () => {
    setIsOpen(false);
    sessionStorage.setItem('voope_pwa_popup_session_dismissed', 'true');
  };

  // Não mostrar novamente
  const handleDismissPermanently = () => {
    setIsOpen(false);
    localStorage.setItem('voope_pwa_popup_dismissed_permanently', 'true');
  };

  // 1. Ativar Notificações
  const handleEnableNotifications = async () => {
    const granted = await pushNotificationService.requestPermission();
    setNotifPermission(pushNotificationService.getPermissionStatus());
    if (granted) {
      setActionSuccess('Notificações push ativadas com sucesso!');
      pushNotificationService.sendNotification('🔔 Notificações Voope Ativadas!', {
        body: 'Você receberá alertas em tempo real de novas OSs e rotas direto no seu aparelho.',
      });
      setTimeout(() => setActionSuccess(null), 3000);
    }
  };

  // 2. Ativar / Testar GPS
  const handleTestGPS = async () => {
    setIsGettingGps(true);
    setGpsFeedbackMsg('Obtendo sinal...');

    if (areaMotorista?.enviarLocalizacaoGps) {
      try {
        const res = await areaMotorista.enviarLocalizacaoGps();
        if (res.success) {
          setGpsStatus('granted');
          setGpsFeedbackMsg(res.message || 'GPS ativo e conectado!');
          setActionSuccess('Localização GPS autorizada!');
        } else {
          setGpsFeedbackMsg(res.message || 'Erro ao capturar GPS');
        }
      } catch {
        setGpsFeedbackMsg('Falha ao acessar GPS');
      } finally {
        setIsGettingGps(false);
      }
    } else {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        setGpsFeedbackMsg('GPS não suportado');
        setIsGettingGps(false);
      } else {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setGpsStatus('granted');
            setGpsFeedbackMsg(`GPS Ativo (±${Math.round(pos.coords.accuracy)}m)`);
            setActionSuccess('Localização GPS autorizada!');
            setIsGettingGps(false);
          },
          (err) => {
            setGpsStatus(err.code === 1 ? 'denied' : 'prompt');
            setGpsFeedbackMsg(err.code === 1 ? 'GPS Bloqueado' : 'Erro ao obter GPS');
            setIsGettingGps(false);
          },
          { enableHighAccuracy: true, timeout: 10000 }
        );
      }
    }

    setTimeout(() => {
      setGpsFeedbackMsg(null);
      setActionSuccess(null);
    }, 4000);
  };

  // 3. Ativar Todas as Permissões em 1 Clique
  const handleEnableAll = async () => {
    // 1º Solicita notificação
    if (notifPermission !== 'granted') {
      await handleEnableNotifications();
    }
    // 2º Solicita GPS
    if (gpsStatus !== 'granted') {
      await handleTestGPS();
    }
    // 3º Se puder instalar, dispara o prompt
    if (!isInstalled && isInstallable) {
      await install();
    } else if (!isInstalled && isIOS) {
      setShowIOSGuide(true);
    }
  };

  const isTransmitting = isGettingGps || Boolean(areaMotorista?.transmitindoGps);

  return (
    <>
      {/* POP-UP MODAL PRINCIPAL (Aparece quando pendente ou chamado pelo usuário) */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="relative w-full max-w-md bg-slate-950 border border-slate-800 rounded-3xl p-5 sm:p-6 shadow-2xl text-slate-100 space-y-5 animate-in zoom-in-95 duration-200">
            
            {/* Header do Pop-up */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-red-600 to-amber-600 flex items-center justify-center text-white shadow-lg shadow-red-600/30 shrink-0">
                  <Smartphone className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-black text-sm text-white uppercase tracking-tight flex items-center gap-1.5">
                    <span>Configurar Aplicativo</span>
                    <span className="px-1.5 py-0.2 bg-red-600/20 text-red-400 border border-red-500/30 text-[9px] rounded font-bold">
                      PWA
                    </span>
                  </h3>
                  <p className="text-xs text-slate-400">Ative as permissões essenciais para a operação em tempo real.</p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleCloseSession}
                className="p-1.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 hover:text-white hover:bg-slate-800 transition cursor-pointer"
                title="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Feedback temporário de sucesso */}
            {actionSuccess && (
              <div className="p-3 rounded-2xl bg-emerald-950/80 border border-emerald-800 text-emerald-300 text-xs font-bold flex items-center gap-2 animate-in fade-in">
                <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>{actionSuccess}</span>
              </div>
            )}

            {/* Lista dos 3 Recursos com Status e Botões Diretos */}
            <div className="space-y-2.5">
              
              {/* 1. Notificações Push */}
              <div className="p-3.5 rounded-2xl bg-slate-900/90 border border-slate-800 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className={`p-2 rounded-xl shrink-0 ${notifPermission === 'granted' ? 'bg-emerald-950 text-emerald-400 border border-emerald-800' : 'bg-slate-800 text-amber-400'}`}>
                    <Bell className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-white">Notificações Push</span>
                      {notifPermission === 'granted' ? (
                        <span className="px-1.5 py-0.2 bg-emerald-950 text-emerald-400 text-[9px] rounded font-extrabold border border-emerald-800 flex items-center gap-0.5">
                          <Check className="w-2.5 h-2.5" /> Ativo
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.2 bg-amber-950 text-amber-400 text-[9px] rounded font-bold border border-amber-800">
                          Pendente
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-400">Avisos sonoros de novas OSs na tela bloqueada.</p>
                  </div>
                </div>

                {notifPermission !== 'granted' ? (
                  <button
                    type="button"
                    onClick={handleEnableNotifications}
                    className="px-3 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs shrink-0 transition cursor-pointer shadow-sm"
                  >
                    Permitir
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => pushNotificationService.sendNotification('🚚 Teste Voope', { body: 'Notificações ativas!' })}
                    className="px-2.5 py-1 rounded-lg bg-slate-800 text-slate-300 hover:text-white text-[11px] font-bold shrink-0 cursor-pointer"
                  >
                    Testar
                  </button>
                )}
              </div>

              {/* 2. Localização GPS */}
              <div className="p-3.5 rounded-2xl bg-slate-900/90 border border-slate-800 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className={`p-2 rounded-xl shrink-0 ${gpsStatus === 'granted' ? 'bg-emerald-950 text-emerald-400 border border-emerald-800' : 'bg-slate-800 text-blue-400'}`}>
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-white">Localização GPS</span>
                      {gpsStatus === 'granted' ? (
                        <span className="px-1.5 py-0.2 bg-emerald-950 text-emerald-400 text-[9px] rounded font-extrabold border border-emerald-800 flex items-center gap-0.5">
                          <Check className="w-2.5 h-2.5" /> Ativo
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.2 bg-slate-800 text-slate-400 text-[9px] rounded font-bold border border-slate-700">
                          {gpsStatus === 'denied' ? 'Bloqueado' : 'Disponível'}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-400">Telemetria de rota e comprovação de paradas.</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleTestGPS}
                  disabled={isTransmitting}
                  className={`px-3 py-1.5 rounded-xl font-bold text-xs shrink-0 transition cursor-pointer ${
                    gpsStatus === 'granted'
                      ? 'bg-slate-800 hover:bg-slate-700 text-blue-300'
                      : 'bg-blue-600 hover:bg-blue-500 text-white shadow-sm'
                  }`}
                >
                  {isTransmitting ? 'Obtendo...' : gpsFeedbackMsg || (gpsStatus === 'granted' ? 'Testar' : 'Permitir')}
                </button>
              </div>

              {/* 3. Instalação do App (PWA) */}
              <div className="p-3.5 rounded-2xl bg-slate-900/90 border border-slate-800 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className={`p-2 rounded-xl shrink-0 ${isInstalled ? 'bg-emerald-950 text-emerald-400 border border-emerald-800' : 'bg-slate-800 text-red-400'}`}>
                    <Download className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-white">Instalação na Tela Inicial</span>
                      {isInstalled ? (
                        <span className="px-1.5 py-0.2 bg-emerald-950 text-emerald-400 text-[9px] rounded font-extrabold border border-emerald-800 flex items-center gap-0.5">
                          <Check className="w-2.5 h-2.5" /> Instalado
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.2 bg-red-950 text-red-400 text-[9px] rounded font-bold border border-red-800">
                          Disponível
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-400">Acesse em tela cheia como um app nativo.</p>
                  </div>
                </div>

                {!isInstalled && isInstallable && (
                  <button
                    type="button"
                    onClick={install}
                    className="px-3 py-1.5 rounded-xl bg-red-600 hover:bg-red-500 text-white font-black text-xs shrink-0 transition cursor-pointer shadow-sm"
                  >
                    Instalar
                  </button>
                )}

                {!isInstalled && isIOS && (
                  <button
                    type="button"
                    onClick={() => setShowIOSGuide(true)}
                    className="px-2.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs shrink-0 transition cursor-pointer"
                  >
                    Ver Guia
                  </button>
                )}

                {isInstalled && (
                  <span className="text-[11px] font-bold text-emerald-400 flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" /> Pronto
                  </span>
                )}
              </div>

            </div>

            {/* Botão Mestre: Ativar Tudo com 1 Clique */}
            {!isAllGranted && (
              <button
                type="button"
                onClick={handleEnableAll}
                className="w-full py-3 px-4 rounded-2xl bg-gradient-to-r from-red-600 via-red-500 to-amber-600 hover:from-red-500 hover:to-amber-500 text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg shadow-red-600/30 transition cursor-pointer"
              >
                <Sparkles className="w-4 h-4" />
                <span>Ativar Tudo com 1 Clique</span>
              </button>
            )}

            {/* Ações de rodapé do pop-up */}
            <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-900 text-slate-400">
              <button
                type="button"
                onClick={handleDismissPermanently}
                className="text-[11px] text-slate-500 hover:text-slate-300 hover:underline cursor-pointer"
              >
                Não mostrar novamente
              </button>

              <button
                type="button"
                onClick={handleCloseSession}
                className="px-3 py-1 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-300 font-bold text-xs cursor-pointer border border-slate-800"
              >
                {isAllGranted ? 'Concluir' : 'Lembrar mais tarde'}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Modal Guia do iPhone/iOS */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm rounded-3xl bg-slate-900 border border-slate-800 p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-red-400" />
                <h3 className="text-sm font-black text-white">Instalar no iPhone / iPad</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowIOSGuide(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="text-xs text-slate-300 space-y-2.5 bg-slate-950 p-4 rounded-2xl border border-slate-800/80">
              <p className="flex items-start gap-2">
                <span className="font-bold text-amber-400">1.</span>
                <span>Abra este site no navegador <strong>Safari</strong> do iPhone.</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="font-bold text-amber-400">2.</span>
                <span>Toque no botão de <strong>Compartilhar</strong> (ícone com quadrado e seta para cima).</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="font-bold text-amber-400">3.</span>
                <span>Role para baixo e selecione <strong>Adicionar à Tela de Início</strong>.</span>
              </p>
            </div>

            <button
              type="button"
              onClick={() => setShowIOSGuide(false)}
              className="w-full py-2.5 rounded-xl bg-red-600 hover:bg-red-500 text-white font-bold text-xs transition cursor-pointer"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </>
  );
};

